import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Typography, Alert, Box, Chip, Divider, LinearProgress, IconButton,
} from '@mui/material';
import { CheckCircle, CloudUpload, Close } from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { profileApi } from '../api/profile.api';
import { FileUploadField } from './FileUploadField';
import { lookupsApi } from '../api/lookups.api';
import { extractApiError } from '../api/error';

/**
 * Shown right after login for dealer / client users whose linked master profile is
 * incomplete: a blocking-but-skippable dialog to fill missing details + upload docs.
 * Renders nothing for staff users (anyone not linked to a dealer/client record).
 */
export function ProfileCompletionGate() {
  const { user } = useAuth();
  const isExternal = user?.linkedEntityType === 'dealer' || user?.linkedEntityType === 'client';

  const { data: check } = useQuery({
    queryKey: ['profile-completion', user?.userId],
    queryFn: () => profileApi.completion(),
    enabled: Boolean(isExternal),
    staleTime: 60_000,
  });

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (check?.applicable && check.completion && !check.completion.complete) setOpen(true);
  }, [check]);

  if (!isExternal || !check?.applicable) return null;
  return <CompletionDialog open={open} onClose={() => setOpen(false)} />;
}

function CompletionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: payload, refetch, isLoading } = useQuery({
    queryKey: ['profile-detail'],
    queryFn: () => profileApi.get(),
    enabled: open,
  });
  const { data: lookups } = useQuery({ queryKey: ['lookups'], queryFn: () => lookupsApi.all(), staleTime: 300_000 });

  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Seed the form from current values whenever the payload (re)loads.
  useEffect(() => {
    if (!payload) return;
    const seed: Record<string, string> = {};
    for (const f of payload.requiredFields) {
      const v = payload.profile[f.key];
      seed[f.key] = v == null ? '' : String(v);
    }
    setForm(seed);
  }, [payload]);

  const uploadedDocTypes = useMemo(() => new Set((payload?.documents ?? []).map((d) => d.docType)), [payload]);

  const saveDetails = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      for (const f of payload!.requiredFields) {
        const raw = form[f.key]?.trim() ?? '';
        if (f.key === 'regionId' || f.key === 'stateId') patch[f.key] = raw ? Number(raw) : null;
        else patch[f.key] = raw;
      }
      return profileApi.update(patch);
    },
    onSuccess: () => { setError(null); refetch(); },
    onError: (e) => setError(extractApiError(e)),
  });

  if (!open) return null;

  const completion = payload?.completion;
  const done = completion?.complete ?? false;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={(_e, reason) => { if (reason !== 'backdropClick') onClose(); }}>
      <DialogTitle>
        Complete your profile
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <LinearProgress />}
        {payload && (
          <Stack spacing={2.5}>
            {done ? (
              <Alert icon={<CheckCircle />} severity="success">
                Your profile is complete. Thank you!
              </Alert>
            ) : (
              <Alert severity="info">
                Welcome, <b>{payload.profile.name}</b>. Please complete your {payload.entity} profile so we can
                activate all features. Fill the details below and upload the required documents.
              </Alert>
            )}

            {/* ---- Details ---- */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Details {completion?.detailsComplete && <CheckCircle color="success" fontSize="small" sx={{ verticalAlign: 'middle' }} />}
              </Typography>
              <Stack spacing={1.5}>
                {payload.requiredFields.map((f) => {
                  if (f.key === 'regionId' || f.key === 'stateId') {
                    const opts = f.key === 'regionId' ? lookups?.regions ?? [] : lookups?.states ?? [];
                    return (
                      <TextField key={f.key} select size="small" label={f.label} value={form[f.key] ?? ''} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}>
                        <MenuItem value="">—</MenuItem>
                        {opts.map((o) => <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>)}
                      </TextField>
                    );
                  }
                  return (
                    <TextField key={f.key} size="small" label={f.label} value={form[f.key] ?? ''} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} />
                  );
                })}
                {error && <Alert severity="error">{error}</Alert>}
                <Button variant="contained" disabled={saveDetails.isPending} onClick={() => saveDetails.mutate()} sx={{ alignSelf: 'flex-start' }}>
                  Save details
                </Button>
              </Stack>
            </Box>

            <Divider />

            {/* ---- Documents ---- */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Documents {completion?.documentsComplete && <CheckCircle color="success" fontSize="small" sx={{ verticalAlign: 'middle' }} />}
              </Typography>
              <Stack spacing={1.5}>
                {payload.requiredDocs.map((d) => (
                  <DocRow key={d.type} docType={d.type} label={d.label} uploaded={uploadedDocTypes.has(d.type)} onUploaded={() => refetch()} />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Finish later'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DocRow({ docType, label, uploaded, onUploaded }: { docType: string; label: string; uploaded: boolean; onUploaded: () => void }) {
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => profileApi.addDocument({ docType, fileName: fileName.trim() || `${docType}.pdf`, fileUrl: fileUrl.trim() }),
    onSuccess: () => { setError(null); setFileName(''); setFileUrl(''); onUploaded(); },
    onError: (e) => setError(extractApiError(e)),
  });

  if (uploaded) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" color="success" icon={<CheckCircle fontSize="small" />} label={label} />
        <Typography variant="caption" color="text.secondary">Uploaded</Typography>
      </Stack>
    );
  }
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>{label}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField size="small" label="Document name" value={fileName} onChange={(e) => setFileName(e.target.value)} sx={{ flex: 1 }} />
        <Box sx={{ flex: 1.5 }}><FileUploadField value={fileUrl || null} onChange={(url) => setFileUrl(url ?? '')} /></Box>
        <Button variant="outlined" startIcon={<CloudUpload />} disabled={!fileUrl.trim() || add.isPending} onClick={() => add.mutate()}>Add</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}
